/**
 * Sticky Sidebar JavaScript Plugin.
 * @version 3.3.4+bg-fixes
 * @author Ahmed Bouhuolia (fixed by Bogdan Gurskyi)
 * @license MIT
 */
const StickySidebar = (() => {
  const EVENT_KEY = '.stickySidebar';
  const VERSION   = '3.3.4';

  const DEFAULTS = {
    topSpacing: 0,
    bottomSpacing: 0,
    containerSelector: false,
    innerWrapperSelector: '.inner-wrapper-sticky',
    stickyClass: 'is-affixed',
    resizeSensor: true,
    minWidth: false
  };

  class StickySidebar{
    constructor(sidebar, options = {}){
      this.options = StickySidebar.extend(DEFAULTS, options);

      this.sidebar = (typeof sidebar === 'string') ? document.querySelector(sidebar) : sidebar;
      if (typeof this.sidebar === 'undefined' || !this.sidebar) {
        throw new Error("There is no specific sidebar element.");
      }

      this.sidebarInner = false;
      this.container = this.sidebar.parentElement;

      this.affixedType = 'STATIC';
      this.direction = 'down';
      this.support = { transform: false, transform3d: false };

      this._initialized = false;
      this._reStyle = false;
      this._breakpoint = false;
      this._running = false;

      this.dimensions = {
        translateY: 0,
        maxTranslateY: 0,
        topSpacing: 0,
        lastTopSpacing: 0,
        bottomSpacing: 0,
        lastBottomSpacing: 0,
        sidebarHeight: 0,
        sidebarWidth: 0,
        containerTop: 0,
        containerHeight: 0,
        viewportHeight: 0,
        viewportTop: 0,
        lastViewportTop: 0,
      };

      // observers
      this._resizeObserver = null;
      this._mutationObserver = null;

      ['handleEvent'].forEach( (method) => {
        this[method] = this[method].bind(this);
      });

      this.initialize();
    }

    initialize(){
      this._setSupportFeatures();

      // Ensure inner wrapper
      if (this.options.innerWrapperSelector) {
        this.sidebarInner = this.sidebar.querySelector(this.options.innerWrapperSelector);
        if (this.sidebarInner === null) this.sidebarInner = false;
      }
      if (!this.sidebarInner) {
        const wrapper = document.createElement('div');
        wrapper.setAttribute('class', 'inner-wrapper-sticky');
        this.sidebar.appendChild(wrapper);
        while (this.sidebar.firstChild !== wrapper) {
          wrapper.appendChild(this.sidebar.firstChild);
        }
        this.sidebarInner = this.sidebar.querySelector('.inner-wrapper-sticky');
      }

      // Container
      if (this.options.containerSelector){
        let containers = Array.prototype.slice.call(document.querySelectorAll(this.options.containerSelector));
        containers.forEach((container) => {
          if (container.contains(this.sidebar)) this.container = container;
        });
        if (!containers.length) throw new Error("The container does not contains on the sidebar.");
      }

      if (typeof this.options.topSpacing !== 'function')
        this.options.topSpacing = parseInt(this.options.topSpacing) || 0;

      if (typeof this.options.bottomSpacing !== 'function')
        this.options.bottomSpacing = parseInt(this.options.bottomSpacing) || 0;

      this._widthBreakpoint();
      this.calcDimensions();
      this.stickyPosition();
      this.bindEvents();

      this._initialized = true;
    }

    bindEvents(){
      window.addEventListener('resize', this, {passive: true, capture: false});
      window.addEventListener('scroll', this, {passive: true, capture: false});

      this.sidebar.addEventListener('update' + EVENT_KEY, this);

      // Prefer modern ResizeObserver to track height changes of content/container.
      if (typeof ResizeObserver !== 'undefined') {
        this._resizeObserver = new ResizeObserver(() => {
          // Recalc when inner content expands/collapses (accordions, etc.)
          this.updateSticky({ type: 'resize' });
        });
        this._resizeObserver.observe(this.sidebarInner);
        this._resizeObserver.observe(this.container);
      } else if (this.options.resizeSensor && typeof ResizeSensor !== 'undefined') {
        // Legacy path if project includes ResizeSensor
        new ResizeSensor(this.sidebarInner, this.handleEvent);
        new ResizeSensor(this.container, this.handleEvent);
      } else if (typeof MutationObserver !== 'undefined') {
        // Fallback: observe DOM mutations that could change height
        this._mutationObserver = new MutationObserver(() => {
          this.updateSticky({ type: 'resize' });
        });
        this._mutationObserver.observe(this.container, { childList: true, subtree: true, attributes: true, characterData: false });
        // Also listen for CSS transitions/animations that end with a new height
        window.addEventListener('transitionend', this, {passive: true});
        window.addEventListener('animationend', this, {passive: true});
      }
    }

    handleEvent(event){
      // Normalize transition/animation end to resize-style updates
      if (event && (event.type === 'transitionend' || event.type === 'animationend')) {
        this.updateSticky({ type: 'resize' });
        return;
      }
      this.updateSticky(event);
    }

    calcDimensions(){
      if (this._breakpoint) return;
      const dims = this.dimensions;

      // Container
      dims.containerTop    = StickySidebar.offsetRelative(this.container).top;
      dims.containerHeight = this.container.clientHeight;
      dims.containerBottom = dims.containerTop + dims.containerHeight;

      // Sidebar
      dims.sidebarHeight = this.sidebarInner.offsetHeight;
      dims.sidebarWidth  = this.sidebarInner.offsetWidth;

      // Viewport
      dims.viewportHeight = window.innerHeight;

      // Max translate
      dims.maxTranslateY = dims.containerHeight - dims.sidebarHeight;

      this._calcDimensionsWithScroll();
    }

    _calcDimensionsWithScroll(){
      const dims = this.dimensions;

      dims.sidebarLeft = StickySidebar.offsetRelative(this.sidebar).left;

      dims.viewportTop    = document.documentElement.scrollTop || document.body.scrollTop;
      dims.viewportBottom = dims.viewportTop + dims.viewportHeight;
      dims.viewportLeft   = document.documentElement.scrollLeft || document.body.scrollLeft;

      dims.topSpacing    = this.options.topSpacing;
      dims.bottomSpacing = this.options.bottomSpacing;

      if (typeof dims.topSpacing === 'function')
        dims.topSpacing = parseInt(dims.topSpacing(this.sidebar)) || 0;

      if (typeof dims.bottomSpacing === 'function')
        dims.bottomSpacing = parseInt(dims.bottomSpacing(this.sidebar)) || 0;

      if (this.affixedType === 'VIEWPORT-TOP') {
        if (dims.topSpacing < dims.lastTopSpacing) {
          dims.translateY += dims.lastTopSpacing - dims.topSpacing;
          this._reStyle = true;
        }
      } else if (this.affixedType === 'VIEWPORT-BOTTOM') {
        if (dims.bottomSpacing < dims.lastBottomSpacing) {
          dims.translateY += dims.lastBottomSpacing - dims.bottomSpacing;
          this._reStyle = true;
        }
      }

      dims.lastTopSpacing    = dims.topSpacing;
      dims.lastBottomSpacing = dims.bottomSpacing;
    }

    isSidebarFitsViewport(){
      const dims = this.dimensions;
      const offset = this.scrollDirection === 'down' ? dims.lastBottomSpacing : dims.lastTopSpacing;
      return (dims.sidebarHeight + offset) < dims.viewportHeight;
    }

    observeScrollDir(){
      const dims = this.dimensions;
      if (dims.lastViewportTop === dims.viewportTop) return;

      const furthest = (this.direction === 'down') ? Math.min : Math.max;
      if (dims.viewportTop === furthest(dims.viewportTop, dims.lastViewportTop))
        this.direction = (this.direction === 'down') ? 'up' : 'down';
    }

    getAffixType(){
      this._calcDimensionsWithScroll();
      const dims = this.dimensions;
      const colliderTop = dims.viewportTop + dims.topSpacing;
      let affixType = this.affixedType;

      if (colliderTop <= dims.containerTop || dims.containerHeight <= dims.sidebarHeight) {
        dims.translateY = 0;
        affixType = 'STATIC';
      } else {
        affixType = (this.direction === 'up') ? this._getAffixTypeScrollingUp()
            : this._getAffixTypeScrollingDown();
      }

      // Clamp translate
      dims.translateY = Math.max(0, Math.min(dims.containerHeight, Math.round(dims.translateY)));

      dims.lastViewportTop = dims.viewportTop;
      return affixType;
    }

    _getAffixTypeScrollingDown(){
      const dims = this.dimensions;
      const sidebarBottom = dims.sidebarHeight + dims.containerTop;
      const colliderTop = dims.viewportTop + dims.topSpacing;
      const colliderBottom = dims.viewportBottom - dims.bottomSpacing;
      let affixType = this.affixedType;

      if (this.isSidebarFitsViewport()){
        if (dims.sidebarHeight + colliderTop >= dims.containerBottom){
          dims.translateY = dims.containerBottom - sidebarBottom;
          affixType = 'CONTAINER-BOTTOM';
        } else if (colliderTop >= dims.containerTop){
          dims.translateY = colliderTop - dims.containerTop;
          affixType = 'VIEWPORT-TOP';
        }
      } else {
        if (dims.containerBottom <= colliderBottom){
          dims.translateY = dims.containerBottom - sidebarBottom;
          affixType = 'CONTAINER-BOTTOM';
        } else if (sidebarBottom + dims.translateY <= colliderBottom){
          dims.translateY = colliderBottom - sidebarBottom;
          affixType = 'VIEWPORT-BOTTOM';
        } else if (dims.containerTop + dims.translateY <= colliderTop &&
            (dims.translateY !== 0 && dims.maxTranslateY !== dims.translateY)){
          affixType = 'VIEWPORT-UNBOTTOM';
        }
      }

      return affixType;
    }

    _getAffixTypeScrollingUp(){
      const dims = this.dimensions;
      const sidebarBottom = dims.sidebarHeight + dims.containerTop;
      const colliderTop = dims.viewportTop + dims.topSpacing;
      const colliderBottom = dims.viewportBottom - dims.bottomSpacing;
      let affixType = this.affixedType;

      if (colliderTop <= dims.translateY + dims.containerTop){
        dims.translateY = colliderTop - dims.containerTop;
        affixType = 'VIEWPORT-TOP';
      } else if (dims.containerBottom <= colliderBottom){
        dims.translateY = dims.containerBottom - sidebarBottom;
        affixType = 'CONTAINER-BOTTOM';
      } else if (!this.isSidebarFitsViewport()){
        if (dims.containerTop <= colliderTop &&
            (dims.translateY !== 0 && dims.maxTranslateY !== dims.translateY)){
          affixType = 'VIEWPORT-UNBOTTOM';
        }
      }

      return affixType;
    }

    _getStyle(affixType){
      if (typeof affixType === 'undefined') return;

      const style = { inner: {}, outer: {} };
      const dims = this.dimensions;

      switch (affixType){
        case 'VIEWPORT-TOP':
          style.inner = {
            position: 'fixed',
            top: dims.topSpacing,
            left: dims.sidebarLeft - dims.viewportLeft,
            width: dims.sidebarWidth
          };
          break;
        case 'VIEWPORT-BOTTOM':
          // FIX: keep left relative to viewport scroll, same as VIEWPORT-TOP
          style.inner = {
            position: 'fixed',
            top: 'auto',
            left: dims.sidebarLeft - dims.viewportLeft,
            bottom: dims.bottomSpacing,
            width: dims.sidebarWidth
          };
          break;
        case 'CONTAINER-BOTTOM':
        case 'VIEWPORT-UNBOTTOM': {
          const translate = this._getTranslate(0, dims.translateY + 'px');
          if (translate)
            style.inner = { transform: translate };
          else
            style.inner = { position: 'absolute', top: dims.translateY, width: dims.sidebarWidth };
          break;
        }
      }

      switch (affixType){
        case 'VIEWPORT-TOP':
        case 'VIEWPORT-BOTTOM':
        case 'VIEWPORT-UNBOTTOM':
        case 'CONTAINER-BOTTOM':
          style.outer = { height: dims.sidebarHeight, position: 'relative' };
          break;
      }

      style.outer = StickySidebar.extend({height: '', position: ''}, style.outer);
      style.inner = StickySidebar.extend({position: 'relative', top: '', left: '',
        bottom: '', width: '', transform: ''}, style.inner);

      return style;
    }

    stickyPosition(force){
      if (this._breakpoint) return;

      force = this._reStyle || force || false;

      const affixType = this.getAffixType();
      const style = this._getStyle(affixType);

      if ((this.affixedType !== affixType || force) && affixType){
        const affixEvent = 'affix.' + affixType.toLowerCase().replace('viewport-', '') + EVENT_KEY;
        StickySidebar.eventTrigger(this.sidebar, affixEvent);

        if (affixType === 'STATIC')
          StickySidebar.removeClass(this.sidebar, this.options.stickyClass);
        else
          StickySidebar.addClass(this.sidebar, this.options.stickyClass);

        for (let key in style.outer){
          const unit = (typeof style.outer[key] === 'number') ? 'px' : '';
          this.sidebar.style[key] = style.outer[key] + unit;
        }

        for (let key in style.inner){
          const unit = (typeof style.inner[key] === 'number') ? 'px' : '';
          this.sidebarInner.style[key] = style.inner[key] + unit;
        }

        const affixedEvent = 'affixed.' + affixType.toLowerCase().replace('viewport-', '') + EVENT_KEY;
        StickySidebar.eventTrigger(this.sidebar, affixedEvent);
      } else {
        if (this._initialized) this.sidebarInner.style.left = style.inner.left;
      }

      this.affixedType = affixType;
    }

    _widthBreakpoint(){
      if (this.options.minWidth && window.innerWidth <= this.options.minWidth){
        this._breakpoint = true;
        this.affixedType = 'STATIC';

        this.sidebar.removeAttribute('style');
        StickySidebar.removeClass(this.sidebar, this.options.stickyClass);
        if (this.sidebarInner) this.sidebarInner.removeAttribute('style');
      } else {
        this._breakpoint = false;
      }
    }

    updateSticky(event = {}){
      if (this._running) return;
      this._running = true;

      ((eventType) => {
        requestAnimationFrame(() => {
          switch (eventType){
            case 'scroll':
              this._calcDimensionsWithScroll();
              this.observeScrollDir();
              this.stickyPosition();
              break;
            case 'resize':
            default:
              this._widthBreakpoint();
              this.calcDimensions();
              this.stickyPosition(true);
              break;
          }
          this._running = false;
        });
      })(event.type);
    }

    _setSupportFeatures(){
      const support = this.support;
      support.transform = StickySidebar.supportTransform(false);
      support.transform3d = StickySidebar.supportTransform(true);
    }

    _getTranslate(y = 0, x = 0, z = 0){
      if (this.support.transform3d) return 'translate3d(' + y +', '+ x +', '+ z +')';
      else if (this.support.transform) return 'translate('+ y +', '+ x +')';
      else return false;
    }

    destroy(){
      window.removeEventListener('resize', this, {capture: false});
      window.removeEventListener('scroll', this, {capture: false});
      window.removeEventListener('transitionend', this, {capture: false});
      window.removeEventListener('animationend', this, {capture: false});

      this.sidebar.classList.remove(this.options.stickyClass);
      this.sidebar.style.minHeight = '';

      this.sidebar.removeEventListener('update' + EVENT_KEY, this);

      const styleReset = {
        inner: { position: '', top: '', left: '', bottom: '', width: '', transform: '' },
        outer: { height: '', position: '' }
      };

      for (let key in styleReset.outer) this.sidebar.style[key] = styleReset.outer[key];
      for (let key in styleReset.inner) this.sidebarInner.style[key] = styleReset.inner[key];

      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
      if (this._mutationObserver) {
        this._mutationObserver.disconnect();
        this._mutationObserver = null;
      }

      if (this.options.resizeSensor && typeof ResizeSensor !== 'undefined') {
        ResizeSensor.detach(this.sidebarInner, this.handleEvent);
        ResizeSensor.detach(this.container, this.handleEvent);
      }
    }

    static supportTransform(transform3d){
      let result = false,
          property = (transform3d) ? 'perspective' : 'transform',
          upper = property.charAt(0).toUpperCase() + property.slice(1),
          prefixes = ['Webkit', 'Moz', 'O', 'ms'],
          support = document.createElement('support'),
          style = support.style;

      (property + ' ' + prefixes.join(upper + ' ') + upper).split(' ').forEach(function(prop) {
        if (style[prop] !== undefined) {
          result = prop;
          return false;
        }
      });
      return result;
    }

    static eventTrigger(element, eventName, data){
      let event;
      try{
        event = new CustomEvent(eventName, {detail: data});
      } catch(e){
        event = document.createEvent('CustomEvent');
        event.initCustomEvent(eventName, true, true, data);
      }
      element.dispatchEvent(event);
    }

    static extend(defaults, options){
      const results = {};
      for (let key in defaults){
        results[key] = (typeof options[key] !== 'undefined') ? options[key] : defaults[key];
      }
      return results;
    }

    static offsetRelative(element){
      const result = {left: 0, top: 0};
      let el = element;
      do{
        const offsetTop = el.offsetTop;
        const offsetLeft = el.offsetLeft;
        if (!isNaN(offsetTop)) result.top  += offsetTop;
        if (!isNaN(offsetLeft)) result.left += offsetLeft;
        el = (el.tagName === 'BODY') ? el.parentElement : el.offsetParent;
      } while(el);
      return result;
    }

    static addClass(element, className){
      if (!StickySidebar.hasClass(element, className)){
        if (element.classList) element.classList.add(className);
        else element.className += ' ' + className;
      }
    }

    static removeClass(element, className){
      if (StickySidebar.hasClass(element, className)){
        if (element.classList) element.classList.remove(className);
        else element.className = element.className.replace(new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
      }
    }

    static hasClass(element, className){
      if (element.classList) return element.classList.contains(className);
      return new RegExp('(^| )' + className + '( |$)', 'gi').test(element.className);
    }

    static get defaults(){ return DEFAULTS; }
  }

  return StickySidebar;
})();

export default StickySidebar;

// Global
window.StickySidebar = StickySidebar;